import AXIOS_INSTANCE from './axiosInstance'

/** WHO growth indicator keys the backend may return (0–5 y standards and the 5–19 y reference). */
export type GrowthIndicatorKey = 'wfa' | 'lhfa' | 'hfa' | 'wfl' | 'wfh' | 'bfa' | 'hcfa' | 'acfa' | (string & {})

/** The five z-score reference lines, keyed by SD. Each line is a list of [x, y] pairs. */
export type GrowthCurveKey = '-3' | '-2' | '0' | '2' | '3'
export type GrowthCurves = Partial<Record<GrowthCurveKey, [number, number][]>>

export type GrowthPoint = {
  date: string
  age_days: number
  value: number
  z: number | null
  /** Optional x position when it is not the age (wfl/wfh: the length/height in cm). */
  x?: number | null
}

export type GrowthIndicator = {
  label: string
  unit: string
  points: GrowthPoint[]
  curves: GrowthCurves
  /** Optional: what the curves' x values are. Inferred on the client when missing. */
  x_unit?: 'days' | 'months' | 'cm'
}

export type GrowthFlag = {
  code: string
  label: string
  severity: 'severe' | 'moderate' | (string & {})
  z: number | null
  indicator: GrowthIndicatorKey
}

export type GrowthResponse = {
  age_days: number | null
  sex: string | null
  /** Only the indicators that apply to the patient's age are present. */
  indicators: { [key: string]: GrowthIndicator }
  flags: GrowthFlag[]
}

/** GET /api/patients/:id/growth — WHO z-scores, reference curves and growth flags. */
export const fetchGrowth = async (patientId: string): Promise<GrowthResponse> => {
  const { data } = await AXIOS_INSTANCE.get<GrowthResponse>(`patients/${patientId}/growth`)
  return {
    age_days: data?.age_days ?? null,
    flags: Array.isArray(data?.flags) ? data.flags : [],
    indicators: data?.indicators ?? {},
    sex: data?.sex ?? null
  }
}
